import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import {
    useService as useAudioDocumentService,
    TTSVoice,
} from "../../services/AudioDocuments";
import { Context as AuthContext } from "../../context/Auth";
import SectionCard from "../../components/SectionCard";
import ErrorAlert from "../../components/ErrorAlert";
import SuccessAlert from "../../components/SuccessAlert";
import { APIRequestError } from "../../services/util";

const MAX_TEXT_LENGTH = 1000000;
const MIN_TEXT_LENGTH = 50; // New minimum character requirement

export default function DashboardCreate() {
    const audioService = useAudioDocumentService();
    const { state: authState } = useContext(AuthContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isAuthenticated = Boolean(authState.user);

    const [text, setText] = useState("");
    const [voice, setVoice] = useState<TTSVoice>(TTSVoice.ALLOY);
    const [loading, setLoading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [showPricingButton, setShowPricingButton] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const createAudioDocument = async () => {
        if (!isAuthenticated) {
            navigate("/login");
            return;
        }

        if (text.trim().length < MIN_TEXT_LENGTH) {
            setUploadError(
                `Please add at least ${MIN_TEXT_LENGTH} characters of text.`,
            );
            return;
        }

        if (text.length > MAX_TEXT_LENGTH) {
            setUploadError(
                `Text is too long (max ${MAX_TEXT_LENGTH} characters)`,
            );
            return;
        }

        try {
            setLoading(true);
            setUploadError(null);
            setSuccessMessage(null);

            await audioService.create({ text, voice });
            setText("");
            navigate("/dashboard/library");
        } catch (err) {
            console.error(err);
            if (err instanceof APIRequestError) {
                // Check if it's an insufficient credits error
                if (
                    err.type === "insufficient_credits_error" ||
                    err.message.includes("characters remaining")
                ) {
                    setUploadError(err.message);
                    setShowPricingButton(true);
                } else {
                    setUploadError(err.message);
                    setShowPricingButton(false);
                }
            } else if (err instanceof Error) {
                setUploadError(err.message);
                setShowPricingButton(false);
            } else {
                setUploadError(
                    "Failed to create audio document. Please try again.",
                );
                setShowPricingButton(false);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container
            disableGutters
            sx={{
                width: "100%",
                mx: "auto",
                maxWidth: {
                    xs: "100%",
                    sm: 800,
                    md: 1000,
                },
                mt: { xs: 1, sm: 1 },
                pb: { xs: 2, sm: 3 },
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Hero section - always shown */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: 2,
                        py: { xs: 4, sm: 6 },
                    }}
                >
                    <Typography variant="h3">Read Text & PDFs Aloud</Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            maxWidth: 600,
                        }}
                    >
                        Upload text or PDFs and listen to them read back to you
                        with natural-sounding AI voices.
                    </Typography>
                    {!isAuthenticated && (
                        <Button
                            component="a"
                            href="/login"
                            variant="contained"
                            size="large"
                            sx={{
                                mt: 1,
                                px: 4,
                                py: 1.5,
                            }}
                        >
                            Try for free
                        </Button>
                    )}
                </Box>

                {successMessage && (
                    <SuccessAlert
                        message={successMessage}
                        onDismiss={() => setSuccessMessage(null)}
                    />
                )}
                {uploadError && (
                    <ErrorAlert
                        message={uploadError}
                        onDismiss={() => {
                            setUploadError(null);
                            setShowPricingButton(false);
                        }}
                        showBillingLink={showPricingButton}
                        onBillingClick={() => navigate("/dashboard/pricing")}
                    />
                )}

                {/* Create new audio document - always shown */}
                <SectionCard>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                        }}
                    >
                        <TextField
                            multiline
                            rows={isMobile ? 12 : 16}
                            label="Enter text to convert to speech"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            disabled={loading} // Only disabled by loading state
                            fullWidth
                            placeholder="Paste or type your text here..."
                            inputProps={{
                                maxLength: MAX_TEXT_LENGTH,
                            }}
                            helperText={`${text.length.toLocaleString()} / ${MAX_TEXT_LENGTH.toLocaleString()} characters`}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Voice</InputLabel>
                            <Select
                                value={voice}
                                label="Voice"
                                onChange={(e) =>
                                    setVoice(e.target.value as TTSVoice)
                                }
                                disabled={loading} // Only disabled by loading state
                            >
                                <MenuItem value={TTSVoice.ALLOY}>
                                    Alloy
                                </MenuItem>
                                <MenuItem value={TTSVoice.ECHO}>Echo</MenuItem>
                                <MenuItem value={TTSVoice.FABLE}>
                                    Fable
                                </MenuItem>
                                <MenuItem value={TTSVoice.ONYX}>Onyx</MenuItem>
                                <MenuItem value={TTSVoice.NOVA}>Nova</MenuItem>
                                <MenuItem value={TTSVoice.SHIMMER}>
                                    Shimmer
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            onClick={createAudioDocument}
                            disabled={loading} // Only disabled by loading state
                            sx={{ alignSelf: "flex-start" }}
                            size="small"
                        >
                            {loading ? "Saving..." : "Save File"}
                        </Button>
                    </Box>
                </SectionCard>
            </Box>
        </Container>
    );
}
